import { I18nProvider } from '@lingui/react';
import { QueryClientProvider, type QueryClient } from '@tanstack/react-query';
import { RouterProvider } from '@tanstack/react-router';
import { useState } from 'react';

import { ConfigProvider, type RuntimeConfig } from '@/shared/config';
import { NotifierProvider, type Notifier } from '@/shared/data';
import { i18n } from '@/shared/i18n/i18n';
import { TelemetryErrorBoundary } from '@/shared/telemetry';
import { FieldConfigProvider } from '@/shared/ui/field-config';
import { toast, Toaster } from '@/shared/ui/toaster';

import { AuthProvider, type AuthSession } from './auth';
import { AppCrash } from './shell/app-crash';
import { createAppRouter } from './router';

const notifier: Notifier = {
  notify: ({ tone, title, description }) => {
    toast[tone](title, description ? { description } : undefined);
  },
};

export function App(props: { queryClient: QueryClient; config: RuntimeConfig; session: AuthSession }) {
  const [router] = useState(() => createAppRouter({ queryClient: props.queryClient }));
  return (
    <I18nProvider i18n={i18n}>
      <TelemetryErrorBoundary fallback={<AppCrash />}>
        <ConfigProvider config={props.config}>
          <AuthProvider session={props.session}>
            <QueryClientProvider client={props.queryClient}>
              <NotifierProvider notifier={notifier}>
                <FieldConfigProvider config={props.config.FORM_FIELDS ?? undefined}>
                  <RouterProvider router={router} />
                  <Toaster />
                </FieldConfigProvider>
              </NotifierProvider>
            </QueryClientProvider>
          </AuthProvider>
        </ConfigProvider>
      </TelemetryErrorBoundary>
    </I18nProvider>
  );
}
